import {
  useBillingCycleDetails,
  useBillingCyclesList,
  useBillTemplatesList,
  useDraftDetails,
  useSaveBillingConfiguration,
  useUpdateBillingConfiguration,
} from "@features/bx/BulkBillGeneration/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { BarChart3, FileText, Info, Loader2, Play } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

interface Step1BillingConfigurationProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

const Step1BillingConfiguration: React.FC<Step1BillingConfigurationProps> = ({
  stepHelpers,
  currentStepIndex = 0,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
}) => {
  const navigate = useNavigate();
  const { id, action } = useParams();
  const { data: draftdetails } = useDraftDetails(id);
  const [showCreateCycleModal, setShowCreateCycleModal] = useState(false);
  const [selectedCycleData, setSelectedCycleData] = useState<any>(null);
  const [isLoadingCycleData, setIsLoadingCycleData] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(data?.billingCycle || "");
  const { data: billingCycles, isLoading: isLoadingCycles } =
    useBillingCyclesList();
  const { data: billTemplates, isLoading: isLoadingTemplates } =
    useBillTemplatesList();
  const { mutateAsync: createBillingCycle, isPending: isCreating } =
    useSaveBillingConfiguration();
  const { data: billingCycleDetails, isLoading: isLoading } =
    useBillingCycleDetails(selectedCycle);
  const { mutateAsync: updateBillingCycle, isPending: isUpdating } =
    useUpdateBillingConfiguration(id);
  const billingCycleOptions = useMemo(() => {
    return (
      billingCycles?.map((cycle: any) => ({
        value: cycle.id,
        label: cycle.billCycleName,
      })) || []
    );
  }, [billingCycles]);

  const billTemplateOptions = useMemo(() => {
    return (
      billTemplates?.map((template: any) => ({
        value: template.id,
        label: template.name,
      })) || []
    );
  }, [billTemplates]);

  const handleCreateNewCycle = () => {
    navigate("/bx/bill-setup/billcycle/action/add");
  };

  // Form fields
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "billingCycle",
        label: "Select Billing Cycle",
        type: "select",
        required: true,
        placeholder: "Choose a billing cycle...",
        options: billingCycleOptions,
        defaultValue: data.billingCycle || "",
        classes: FIELD_CLASSES,
        tooltipText: "Select the billing cycle group to generate bills for...",
        showHelperTooltip: true,
        className: "col-span-3",
      },
      {
        name: "createCycleBtn",
        label: "",
        type: "button",
        buttonLabel: "Create New Cycle",
        buttonIcon: <Play className="h-4 w-4" />,
        buttonVariant: "outline",
        onClick: handleCreateNewCycle,
        className: "col-span-1 mt-1.5",
      },
      {
        name: "billingPeriodStart",
        label: "Billing Period Start",
        type: "datepicker",
        required: true,
        defaultValue: data.billingPeriodStart || "",
        classes: FIELD_CLASSES,
        tooltipText: "The start date of the billing period for this cycle",
        showHelperTooltip: true,
        className: "col-span-2",
      },
      {
        name: "billingPeriodEnd",
        label: "Billing Period End",
        type: "datepicker",
        required: true,
        defaultValue: data.billingPeriodEnd || "",
        classes: FIELD_CLASSES,
        tooltipText: "The end date of the billing period for this cycle",
        showHelperTooltip: true,
        className: "col-span-2",
      },
      {
        name: "billTemplate",
        label: "Bill Template",
        type: "select",
        required: true,
        placeholder: "Select a bill template...",
        options: billTemplateOptions,
        defaultValue: data.billTemplate || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "Choose the template format for bill generation. Different templates provide varying levels of detail.",
        className: "col-span-4",
      },
    ],
    [billingCycleOptions, billTemplateOptions, selectedCycle],
  );

  const form = FormService.createForm({
    fields: formFields,
  });

  useEffect(() => {
    FormService.populateForm(
      form,
      {
        billingCycle: draftdetails?.billDraftData?.cycleId,
        billingPeriodStart:
          draftdetails?.billDraftData?.step_1Config?.invoiceStartDate,
        billingPeriodEnd:
          draftdetails?.billDraftData?.step_1Config?.invoiceEndDate,
        billTemplate: draftdetails?.billDraftData?.billConfigId,
      },
      formFields,
    );
  }, [form, draftdetails]);

  const formValues = form.watch();

  useEffect(() => {
    if (formValues.billingCycle && formValues.billingCycle !== selectedCycle) {
      setSelectedCycle(formValues.billingCycle);
    }
  }, [formValues.billingCycle, selectedCycle]);

  const summaryItems = [
    {
      label: "Cycle Name",
      value:
        billingCycleOptions.find(
          (c: any) => String(c.value) === String(formValues.billingCycle),
        )?.label ||
        billingCycleDetails?.billCycleName ||
        "-",
    },
    {
      label: "Billing Period",
      value: `${formValues.billingPeriodStart || "-"} to ${
        formValues.billingPeriodEnd || "-"
      }`,
    },
    {
      label: "Bill Template",
      value:
        billTemplateOptions.find(
          (t: any) => String(t.value) === String(formValues.billTemplate),
        )?.label ||
        formValues.billTemplate ||
        "-",
    },
    {
      label: "Category",
      // categoryName is an array ["Residential"], so we join it
      value: billingCycleDetails?.categoryName?.join(", ") || "-",
    },
    {
      label: "Sub Category",
      // use subCategoryName to get "Single-Family Homes" instead of the raw subCategory with IDs
      value: billingCycleDetails?.subCategoryName?.join(", ") || "-",
    },
    {
      label: "Consumer",
      value: billingCycleDetails?.consumerCount?.toString() || "-",
    },
    {
      label: "Meter",
      value: billingCycleDetails?.totalMeterCount ?? "-",
    },
    {
      label: "Premises",
      value: billingCycleDetails?.totalPremiseCount ?? "-",
    },
  ];

  // Simulate loading cycle comparison data
  useEffect(() => {
    if (formValues.billingCycle) {
      setIsLoadingCycleData(true);

      // Simulate API call delay
      const timer = setTimeout(() => {
        const cycle = billingCycles?.find(
          (c: any) => c.id === formValues.billingCycle,
        );
        setSelectedCycleData(cycle);
        setIsLoadingCycleData(false);
      }, 800);

      return () => clearTimeout(timer);
    } else {
      setSelectedCycleData(null);
      setIsLoadingCycleData(false);
    }
  }, [formValues.billingCycle, billingCycles]);

  // Calculate end date based on start date and frequencyDisplay
  const calculateEndDate = useCallback((startDateValue: string, frequencyDisplay: string): string => {
    if (!startDateValue || !frequencyDisplay) return "";

    const start = dayjs(startDateValue);
    if (!start.isValid()) return "";

    const display = frequencyDisplay.toLowerCase().trim();
    let end = start;

    if (display === "weekly") {
      end = end.add(7, "day");
    } else if (display === "bi-monthly" || display === "bimonthly") {
      end = end.add(60, "day"); // Using 60 days for Bi-Monthly as per Step1BasicInfo
    } else if (display === "monthly") {
      end = end.add(1, "month");
    } else if (display === "quarterly") {
      end = end.add(3, "month");
    } else if (display === "half-yearly" || display === "half yearly") {
      end = end.add(6, "month");
    } else if (display === "yearly") {
      end = end.add(1, "year");
    } else if (display === "daily") {
      end = end.add(1, "day");
    } else {
      return "";
    }

    return end.format("YYYY-MM-DD");
  }, []);

  // Effect to auto-calculate end date when start date or billing cycle changes
  useEffect(() => {
    const start = formValues.billingPeriodStart;
    const cycle = billingCycles?.find(
      (c: any) => String(c.id) === String(formValues.billingCycle),
    );
    const frequencyDisplay = cycle?.frequencyDisplay || billingCycleDetails?.frequencyDisplay;

    if (start && frequencyDisplay) {
      const calculatedEndDate = calculateEndDate(start, frequencyDisplay);
      if (calculatedEndDate && calculatedEndDate !== formValues.billingPeriodEnd) {
        form.setValue("billingPeriodEnd", calculatedEndDate);
        
        // Ensure data context is updated for stepper state
        const updatedData = { ...data, billingPeriodEnd: calculatedEndDate };
        onDataChange?.(updatedData);
        if (stepHelpers) {
          stepHelpers.setStepData(currentStepIndex, updatedData);
        }
      }
    }
  }, [
    formValues.billingPeriodStart, 
    formValues.billingCycle, 
    formValues.billingPeriodEnd,
    billingCycles, 
    billingCycleDetails, 
    calculateEndDate, 
    data, 
    onDataChange, 
    stepHelpers, 
    currentStepIndex
  ]);

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      if (fieldName == "billingCycle") {
        setSelectedCycle(value);
      }

      const updatedData = { ...data, [fieldName]: value };
      onDataChange?.(updatedData);

      if (stepHelpers) {
        stepHelpers.setStepData(currentStepIndex, updatedData);
      }

      if (clearValidationError) {
        clearValidationError();
      }
    },
    [data, onDataChange, stepHelpers, currentStepIndex, clearValidationError],
  );

  // Open modal (alternative approach if you want inline creation)
  const handleOpenCreateModal = () => {
    setShowCreateCycleModal(true);
  };

  const handleNext = async () => {
    const isValid = await FormService.validateForm(form);

    if (!isValid) {
      setValidationError?.("Please fill in all required fields.");
      return;
    }

    // Date validation
    const startDate = new Date(formValues.billingPeriodStart);
    const endDate = new Date(formValues.billingPeriodEnd);

    if (endDate <= startDate) {
      setValidationError?.("End date must be after start date.");
      return;
    }

    clearValidationError?.();

    const formData = form.getValues();

    try {
      const selectedTemplate = billTemplates?.find(
        (t: any) => String(t.id) === String(formData.billTemplate),
      );

      const payload = {
        cycle_id: formData.billingCycle,
        billing_period_start: formData.billingPeriodStart,
        billing_period_end: formData.billingPeriodEnd,
        bill_config_id: formData.billTemplate,
        remote_utility_id: getRemoteUtilityId(),
        bill_config_type: selectedTemplate?.type,
        step: {
          step_working_on: 0,
          total_steps: 5,
        },
      };

      let response;

      if (action === "edit") {
        response = await updateBillingCycle(payload);
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.bx.bulkBilling.draftDetails(
            response?.id,
          ),
        });
      } else {
        response = await createBillingCycle(payload);
      }

      if (response) {
        const completedData = {
          ...formData,
          selectedCycleData,
          payload,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        };

        if (stepHelpers) {
          stepHelpers.setStepData(currentStepIndex, completedData);
        }

        onDataChange?.(completedData);
        onNext?.();
        navigate(
          `/bx/bulk-billing/create/${response.id}/${formData.billingCycle}/edit#fetch-consumer`,
        );
      }
    } catch (error) {
      console.error("Failed to save billing configuration:", error);
      setValidationError?.("Failed to save. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex-shrink-0 mt-0.5">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Info className="w-5 h-5 text-blue-600" />
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-blue-900 mb-1">
            Important: Billing Cycle Setup
          </h4>
          <p className="text-sm text-blue-800">
            For a consumer's first bill, the activation date must fall between
            the invoice period start and end dates. Configure the billing cycle
            parameters including dates, frequency, and template settings below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side - Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    Billing Configuration
                  </h3>
                  <p className="text-sm text-gray-600">
                    Configure the billing cycle, period, and template for this
                    batch
                  </p>
                </div>
              </div>

              {/* Loading State */}
              {(isLoadingCycles || isLoadingTemplates) && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-gray-600">
                    Loading configuration options...
                  </span>
                </div>
              )}

              {/* Form */}
              {!isLoadingCycles && !isLoadingTemplates && (
                <>
                  <DynamicForm
                    fields={formFields}
                    form={form}
                    onFieldChange={handleFieldChange}
                    gridColumns={2}
                    responsiveColumns={{
                      sm: 1,
                      md: 4,
                    }}
                    config={{
                      showSubmitButton: false,
                      showResetButton: false,
                      spacing: "medium",
                    }}
                  />

                  {/* Cycle Comparison & History */}
                  {selectedCycle && (
                    <div className=" p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-4 h-4 text-gray-600" />
                        <h4 className="font-medium">
                          Cycle Comparison & History
                        </h4>
                      </div>

                      {isLoadingCycleData ? (
                        <div className="flex items-center justify-center py-2">
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                          <span className="ml-2 text-sm text-gray-600">
                            Loading cycle data...
                          </span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-gray-600 mb-1">
                              Previous Period
                            </p>
                            <p className="text-sm font-medium text-gray-900">
                              {billingCycleDetails?.billingPeriodStartDate}-
                              {billingCycleDetails?.billingPeriodEndDate}
                            </p>
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-gray-600 mb-1">
                              Days Since Last Billing
                            </p>
                            <p className="text-sm font-medium text-gray-900">
                              {billingCycleDetails?.daysSinceLastRun || "0"}{" "}
                              Days
                            </p>
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-gray-600 mb-1">
                              Actual/Estimated Bills
                            </p>
                            <p className="text-sm font-medium text-gray-900">
                              {billingCycleDetails?.billStatistics?.actualBillsCount || 0} /{" "}
                              {billingCycleDetails?.billStatistics?.estimatedBillsCount || 0}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Live Preview Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardContent className="p-6">
              {/* Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 bg-gray-100 rounded">
                  <FileText className="w-4 h-4 text-gray-700" />
                </div>

                <div>
                  <h3 className="font-semibold">Configuration Summary</h3>
                  <p className="text-xs text-gray-600">
                    Overview of selected settings
                  </p>
                </div>
              </div>

              {/* Loading State */}
              {(isLoadingCycles || isLoadingTemplates) && (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400 mb-2" />
                  <p className="text-xs text-gray-500">Loading summary...</p>
                </div>
              )}

              <div className="space-y-1">
                {summaryItems.map((item) => (
                  <SummaryItem
                    key={item.label}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleNext} isLoading={isCreating}>
          Next: Consumer Selection
        </Button>
      </div>
    </div>
  );
};

// Summary Item Component with loading state
const SummaryItem = ({
  label,
  value,
  highlight = false,
  isLoading = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  isLoading?: boolean;
}) => (
  <div
    className={`flex justify-between items-center text-sm border-b pb-2 transition-colors ${
      highlight
        ? "border-blue-200 bg-blue-50 -mx-2 px-2 py-2 rounded"
        : "border-gray-100"
    }`}
  >
    <span className="text-gray-600 p-4">{label}</span>
    {isLoading ? (
      <div className="flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
        <span className="text-xs text-gray-400">Loading...</span>
      </div>
    ) : (
      <span
        className={`font-medium ${
          highlight ? "text-blue-900" : "text-gray-900"
        }`}
      >
        {value}
      </span>
    )}
  </div>
);

export default Step1BillingConfiguration;
